import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import {
  isEditorNode,
  RichTextEditor,
} from 'components/atoms/rich-text-editor';
import { RTE } from 'components/atoms/rte';
import { ActionBar } from 'components/molecules/action-bar';
import { MentionData } from 'components/molecules/mention';
import {
  ProjectVariablesTrigger,
  VariablesSelect,
} from 'components/molecules/variables/variables-select';
import { TemplateObjectType } from 'data/templates/api';
import { useExponentReRender } from 'hooks/use-exponent-rerender';
import { useLatest, useLatestRef } from 'hooks/use-latest';
import { useTranslation } from 'hooks/use-translation';
import { logger } from 'services';
import { queueMacrotask } from 'utils/macrotask';
import styles from './editor.module.css';
import { formatConditionalVariables } from './utils';

const StyledActionBar = styled(ActionBar)`
  height: var(--height-lg);
`;

export const TaskDescriptionEditor: FC<{
  mentionData?: MentionData;
  taskDescription?: string;
  isEditorFocused?: boolean;
  updateTask(taskDescription: string): void;
  onMentionAdd?(data: any): void;
  projectId?: number;
  isPrivate?: boolean;
  placeholder?: string;
  templateType?: TemplateObjectType;
  disabled?: boolean;
  removeUnfinishedVariables?: boolean;
}> = ({
  mentionData,
  taskDescription = '',
  onMentionAdd,
  updateTask,
  projectId,
  isEditorFocused,
  isPrivate,
  placeholder,
  templateType,
  disabled,
  removeUnfinishedVariables = false,
}) => {
  const translatedDescriptionPlaceholderText = useTranslation(
    'task.description.placeholder',
  );
  const [shouldRenderRTE, reRenderRTE, exponentRetryTimes, isExceeded] =
    useExponentReRender(5);

  const [rte, setRte] = useState<RichTextEditor | null>(null);
  const [html, setHtml] = useState(
    removeUnfinishedVariables
      ? formatConditionalVariables(taskDescription)
      : taskDescription,
  );
  const [readonly, setReadonly] = useState(true);

  // CKE on change events listener doesn't update whenever setRteContent changes. So we use ref inside onchange
  const readOnlyRef = useLatestRef(readonly);
  const updateTaskRef = useLatest(updateTask);

  const setRteContent = useCallback(
    (_html) => {
      /**
       * when img is still uploading and user navigates outside the task description. task description would be stored in this formate
       * <figure class="image"><img></figure>
       * however once the imag is uploaded to backend cke would convert the img element in this way
       * <figure class="image"><img src="<img-src>"></figure>
       * this wouldn't be updated in the task description because we only update task description during blur event.
       *  */
      if (readOnlyRef.current) {
        updateTaskRef(_html);
      }

      return setHtml(formatConditionalVariables(_html));
    },
    [readOnlyRef, updateTaskRef],
  );

  const focusEditor = useCallback(() => {
    setReadonly(false);
    queueMacrotask(() => rte?.focus());
  }, [setReadonly, rte]);

  const onRteInteraction = () => {
    if (!readonly) {
      return;
    }
    focusEditor();
  };

  useEffect(() => {
    if (isEditorFocused) {
      focusEditor();
    }
  }, [focusEditor, isEditorFocused]);

  const onDescriptionBlur = () => {
    setReadonly(true);
    updateTask(html);
  };

  useEffect(() => {
    setHtml(
      removeUnfinishedVariables
        ? formatConditionalVariables(taskDescription)
        : taskDescription,
    );
  }, [removeUnfinishedVariables, taskDescription]);

  const getEditorProps = () => {
    return {
      className: styles.description__rte,
      ref: setRte,
      placeholder: placeholder || translatedDescriptionPlaceholderText,
      data: html,
      mentionData,
      onChange: setRteContent,
      onMentionAdd,
      templateType,
      onFocus: onRteInteraction,
    };
  };

  return (
    <OutsideClickHandler
      allowed={(el) => isEditorNode(el)}
      disabled={readonly}
      onOutsideClick={onDescriptionBlur}
    >
      <section
        data-cy="task_description.editor"
        data-cy-private={isPrivate ? 'private' : 'public'}
        className={`${styles.description} ${
          readonly ? styles.readonly : styles.description__edit
        }`}
        role="presentation"
        data-preview-disable="true"
      >
        {projectId && shouldRenderRTE ? (
          <RTE
            {...getEditorProps()}
            projectId={ProjectId(projectId)}
            onError={(e) => {
              logger.error(
                `failed to create task description retry count ${exponentRetryTimes} ${e.stack}`,
                {
                  error: e,
                },
              );
              if (isExceeded) {
                // eslint-disable-next-line no-console
                console.log(
                  'maximum tries exceed to re generate task description',
                  e,
                );
                logger.error(
                  `maximum tries exceed to re generate task description ${e.stack}`,
                  {
                    error: e,
                  },
                );

                return;
              }
              reRenderRTE();
            }}
            readonly={disabled}
          />
        ) : (
          <RichTextEditor
            {...getEditorProps()}
            allowConditionalVariables
            readonly={disabled}
          />
        )}
        <StyledActionBar
          className={readonly ? styles.hide : ''}
          private={isPrivate}
        >
          <ActionBar.Secondary>
            {projectId ? (
              <ProjectVariablesTrigger editor={rte?.editor} />
            ) : (
              <VariablesSelect
                editor={rte?.editor}
                btnClassName="project-template-trigger"
              />
            )}
            <RichTextEditor.Checklist
              enabled
              onSelect={() => rte?.insertChecklist()}
            />
            <RichTextEditor.InsertImage
              enabled
              onSelect={(images) => rte?.insertImages(images)}
            />
            <RichTextEditor.EmojiPicker
              enabled
              onSelect={(emoji) => rte?.addEmoji(emoji)}
            />
          </ActionBar.Secondary>
        </StyledActionBar>
      </section>
    </OutsideClickHandler>
  );
};
